/**
 * CBC Utility Functions
 * Standardizes competency level calculations across the platform.
 */

export const CBC_LEVELS = [
    { 
        code: 'EE', 
        label: 'Exceeding Expectations', 
        min: 70, 
        color: 'text-[#FFD700]', 
        bg: 'bg-[#FFD700]/5', 
        border: 'border-[#FFD700]/20',
        hex: '#FFD700',
        rgb: [255, 215, 0]
    },
    { 
        code: 'ME', 
        label: 'Meeting Expectations', 
        min: 50, 
        color: 'text-[#40E0D0]', 
        bg: 'bg-[#40E0D0]/5', 
        border: 'border-[#40E0D0]/20',
        hex: '#40E0D0',
        rgb: [64, 224, 208]
    },
    { 
        code: 'AE', 
        label: 'Approaching Expectations', 
        min: 30, 
        color: 'text-[#E97451]', 
        bg: 'bg-[#E97451]/5', 
        border: 'border-[#E97451]/20',
        hex: '#E97451',
        rgb: [233, 116, 81]
    },
    { 
        code: 'BE', 
        label: 'Below Expectations', 
        min: 0, 
        color: 'text-[#808080]', 
        bg: 'bg-[#808080]/5', 
        border: 'border-[#808080]/20',
        hex: '#808080',
        rgb: [128, 128, 128]
    }
];

export const COMPETENCY_PERCENTAGES = {
    'EE': 0.8,
    'ME': 0.6,
    'AE': 0.4,
    'BE': 0.2
};

/**
 * Calculates a suggested numeric score based on a competency level.
 * @param {string} level - EE, ME, AE, or BE
 * @param {number} totalMarks - Total possible marks for the assignment
 * @returns {number} - The calculated score
 */
export const getScoreFromLevel = (level, totalMarks) => {
    const percentage = COMPETENCY_PERCENTAGES[level] || 0;
    const validTotal = (totalMarks && totalMarks > 0) ? totalMarks : 100;
    return Math.round(validTotal * percentage);
};


export const calculateLevel = (score, total, bandRanges = []) => {
    const validTotal = (total && total > 0) ? total : 100;
    const percentage = (score / validTotal) * 100;

    if (bandRanges && bandRanges.length > 0) {
        const code = calculate8Level(percentage, bandRanges);
        return code.substring(0, 2); // Return EE, ME, AE, or BE
    }

    if (percentage >= 70) return 'EE';
    if (percentage >= 50) return 'ME';
    if (percentage >= 30) return 'AE';
    return 'BE';
};

/**
 * Maps a percentage to the new 8-level competency scale.
 * Logic mirrors backend precise_averaging.py
 */
export const calculate8Level = (percentage, bandRanges = []) => {
    const p = parseFloat(percentage || 0);

    if (bandRanges && bandRanges.length > 0) {
        // Find the band where the percentage falls within [min, max]
        const match = bandRanges.find(b => p >= b.min_percent && p <= b.max_percent);
        if (match) return match.band;
        
        // Fallback for edge cases (e.g. slightly above 100 or below 0)
        if (p >= 100) return bandRanges[0].band; // Usually EE1
        if (p <= 0) return bandRanges[bandRanges.length - 1].band; // Usually BE2
    }

    // Static Fallback Logic
    if (p >= 90) return 'EE1';
    if (p >= 75) return 'EE2';
    if (p >= 65) return 'ME1';
    if (p >= 50) return 'ME2';
    if (p >= 40) return 'AE1';
    if (p >= 30) return 'AE2';
    if (p >= 20) return 'BE1';
    return 'BE2';
};

/**
 * Maps a percentage to a 0.0 - 8.0 scale mean score.
 * Logic mirrors backend precise_averaging.py with dynamic interpolation.
 */
export const calculateMeanScore = (percentage, bandRanges = []) => {
    if (percentage === null || percentage === undefined) return 1.0;
    const p = parseFloat(percentage);

    if (bandRanges && bandRanges.length > 0) {
        const match = bandRanges.find(b => p >= b.min_percent && p <= b.max_percent);
        if (match) {
            // Dynamic Interpolation Logic:
            // MinMS + ((Score - Min%) / (Max% - Min%)) * (MaxMS - MinMS)
            
            if (match.max_percent === match.min_percent || match.max_ms === match.min_ms) {
                return match.min_ms;
            }
            
            const rangeP = match.max_percent - match.min_percent;
            const rangeMS = match.max_ms - match.min_ms;
            const scoreOffset = p - match.min_percent;
            
            const ms = match.min_ms + (scoreOffset / rangeP) * rangeMS;
            return Math.round(ms * 100) / 100;
        }
        
        // Edge cases
        if (p >= 100) return 8.0;
        if (p <= 0) return 1.0;
    }
    
    // Legacy Logic A (Static Fallback): 
    if (p >= 80) return 8.0;
    if (p < 10) return 1.0;
    return Math.round((p / 10) * 100) / 100;
};

export const getLevelData = (code) => {
    // Handle 8-level codes by stripping the suffix. Stringify to handle numeric Mean Scores.
    const baseCode = String(code || '')?.substring(0, 2).toUpperCase();
    return CBC_LEVELS.find(l => l.code === baseCode) || CBC_LEVELS[2];
};

/**
 * Gets the minimum percentage for a category (EE, ME, AE, BE) from dynamic bands.
 */
export const getCategoryMin = (category, bandRanges = []) => {
    if (bandRanges && bandRanges.length > 0) {
        // Categories are the first 2 chars of the band (e.g., 'EE1' -> 'EE')
        const categoryBands = bandRanges.filter(b => b.band.startsWith(category));
        if (categoryBands.length > 0) {
            return Math.min(...categoryBands.map(b => b.min_percent));
        }
    }
    
    // Fallback to static defaults
    const level = CBC_LEVELS.find(l => l.code === category);
    return level ? level.min : 0;
};

/**
 * Gets the maximum percentage for a category (EE, ME, AE, BE) from dynamic bands.
 */
export const getCategoryMax = (category, bandRanges = []) => {
    if (bandRanges && bandRanges.length > 0) {
        const categoryBands = bandRanges.filter(b => b.band.startsWith(category));
        if (categoryBands.length > 0) {
            return Math.max(...categoryBands.map(b => b.max_percent));
        }
    }
    
    // Fallback to static defaults based on next level's min or 100
    if (category === 'EE') return 100;
    if (category === 'ME') return 69.99;
    if (category === 'AE') return 49.99;
    return 29.99;
};

/**
 * Gets the median percentage for a category from dynamic bands.
 */
export const getCategoryMedian = (category, bandRanges = []) => {
    const min = getCategoryMin(category, bandRanges);
    const max = getCategoryMax(category, bandRanges);
    return (min + max) / 2;
};
